import {
  IncidentClosure,
  IncidentEntity,
  IncidentInput,
  IncidentPatch,
  IncidentPauseInput,
  IncidentReportAvailability,
  IncidentReportDraft,
  IncidentReportSendInput,
  IncidentReportSendResult,
  IncidentWorkEntity,
  ProductManager,
  ReportEntity,
} from '@data/utils/types';
import i18n from 'i18next';
import { toast } from 'sonner';
import {
  QueryClient,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import type { IError, ListResponse } from '@data';
import * as api from '../api';
import * as queryKeys from '../constants/keys';
import { invalidatePmProductLists } from './pm-product-lists';
import { useCreateReport } from './reports';

/** Both lists, always: an incident written on a product tab has to show up on the queue as well. */
const invalidateIncidents = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: [queryKeys.ORGANIZATION_INCIDENTS] });
  queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCT_INCIDENTS] });
};

export const useOrganizationIncidents = (
  organizationId?: string,
  params?: { open?: boolean },
  /** Off until the reader asks for the list: the whole log is only wanted by the archive. */
  enabled = true,
): UseQueryResult<ListResponse<IncidentEntity>, IError> =>
  useQuery({
    // The flag is part of the key: the queue and the nav badge ask for different lists.
    queryKey: [queryKeys.ORGANIZATION_INCIDENTS, organizationId, params?.open ?? false],
    queryFn: () => api.fetchOrganizationIncidents(organizationId!, params),
    enabled: !!organizationId && enabled,
  });

export const useProductIncidents = (productId?: string): UseQueryResult<ListResponse<IncidentEntity>, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_INCIDENTS, productId],
    queryFn: () => api.fetchProductIncidents(productId!),
    enabled: !!productId,
  });

export const useCreateIncident = (): UseMutationResult<
  IncidentEntity,
  IError,
  { productId: string; values: IncidentInput },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, values }) => api.createIncident(productId, values),
    onSettled: () => invalidateIncidents(queryClient),
  });
};

export const useUpdateIncident = (): UseMutationResult<
  IncidentEntity,
  IError,
  { productId: string; incidentId: string; values: IncidentPatch },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, incidentId, values }) => api.updateIncident(productId, incidentId, values),
    onSettled: () => invalidateIncidents(queryClient),
  });
};

export const useCloseIncident = (): UseMutationResult<
  IncidentEntity,
  IError,
  { productId: string; incidentId: string; values: IncidentClosure },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, incidentId, values }) => api.closeIncident(productId, incidentId, values),
    onSettled: () => invalidateIncidents(queryClient),
  });
};

export const useReopenIncident = (): UseMutationResult<
  IncidentEntity,
  IError,
  { productId: string; incidentId: string },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, incidentId }) => api.reopenIncident(productId, incidentId),
    onSettled: () => invalidateIncidents(queryClient),
  });
};

export const useDeleteIncident = (): UseMutationResult<
  void,
  IError,
  { productId: string; incidentId: string },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, incidentId }) => api.deleteIncident(productId, incidentId),
    onSettled: () => invalidateIncidents(queryClient),
  });
};

export const useIncidentWork = (
  productId?: string,
  incidentId?: string,
): UseQueryResult<ListResponse<IncidentWorkEntity>, IError> =>
  useQuery({
    queryKey: [queryKeys.INCIDENT_WORK, productId, incidentId],
    queryFn: () => api.fetchIncidentWork(productId!, incidentId!),
    enabled: !!productId && !!incidentId,
  });

/** No feed of days is on screen where these are used, so the writes fold into no report window. */
const NO_REPORT_WINDOW = {};

/** Booking time from the incident's page, through the day's own write, so it stays one ledger. */
export const useCreateIncidentWork = (
  userId: string,
  organizationId: string,
): ((values: Partial<ReportEntity>) => Promise<ReportEntity>) => {
  const queryClient = useQueryClient();
  const { mutateAsync } = useCreateReport(userId, organizationId, NO_REPORT_WINDOW, false);

  return async values => {
    const report = await mutateAsync(values);
    queryClient.invalidateQueries({ queryKey: [queryKeys.INCIDENT_WORK] });
    invalidateIncidents(queryClient);
    return report;
  };
};

/** Correcting an hour from the incident's page; adds the two caches the sprint hooks know nothing about. */
export const useUpdateIncidentWork = (): UseMutationResult<ReportEntity, IError, Partial<ReportEntity>, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Partial<ReportEntity>) => api.updateReport(values),
    onSuccess: () => toast.success(i18n.t('reportSaved')),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.INCIDENT_WORK] });
      // The entry is on somebody's day too, and the sidebar never unmounts, so its readers go with it.
      queryClient.invalidateQueries({ queryKey: [queryKeys.REPORTS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.UNREPORTED_DAYS] });
      invalidatePmProductLists(queryClient);
      invalidateIncidents(queryClient);
    },
  });
};

export const useRemoveIncidentWork = (): UseMutationResult<void, IError, string, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) => api.removeReport(reportId),
    onSuccess: () => toast.success(i18n.t('reportDeleted')),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.INCIDENT_WORK] });
      // The entry is on somebody's day too, and the sidebar never unmounts, so its readers go with it.
      queryClient.invalidateQueries({ queryKey: [queryKeys.REPORTS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.UNREPORTED_DAYS] });
      invalidatePmProductLists(queryClient);
      invalidateIncidents(queryClient);
    },
  });
};

export const usePauseIncident = (): UseMutationResult<
  IncidentEntity,
  IError,
  { productId: string; incidentId: string; values: IncidentPauseInput },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, incidentId, values }) => api.pauseIncident(productId, incidentId, values),
    onSettled: () => invalidateIncidents(queryClient),
  });
};

/** Ending a pause is this too: both write what the stretch actually was. */
export const useUpdateIncidentPause = (): UseMutationResult<
  IncidentEntity,
  IError,
  { productId: string; incidentId: string; pauseId: string; values: Partial<IncidentPauseInput> },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, incidentId, pauseId, values }) =>
      api.updateIncidentPause(productId, incidentId, pauseId, values),
    onSettled: () => invalidateIncidents(queryClient),
  });
};

export const useDeleteIncidentPause = (): UseMutationResult<
  IncidentEntity,
  IError,
  { productId: string; incidentId: string; pauseId: string },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, incidentId, pauseId }) => api.deleteIncidentPause(productId, incidentId, pauseId),
    onSettled: () => invalidateIncidents(queryClient),
  });
};

/** Which pairs will produce a report, asked once per selection and disabled until something is chosen. */
export const useIncidentReportAvailability = (
  organizationId?: string,
  productIds: string[] = [],
  sprintIds: string[] = [],
): UseQueryResult<ListResponse<IncidentReportAvailability>, IError> =>
  useQuery({
    queryKey: [queryKeys.INCIDENT_REPORT_AVAILABILITY, organizationId, [...productIds].sort(), [...sprintIds].sort()],
    queryFn: () => api.fetchIncidentReportAvailability(organizationId!, productIds, sprintIds),
    enabled: !!organizationId && productIds.length > 0 && sprintIds.length > 0,
  });

/** One report as an object URL, held while the query is cached: revoking on unmount blanks a tab they come back to. */
export const useIncidentReportPdfUrl = (
  productId?: string,
  sprintId?: string,
): UseQueryResult<{ url: string; size: number }, IError> =>
  useQuery({
    queryKey: [queryKeys.INCIDENT_REPORT_PDF, productId, sprintId],
    // Read off the blob rather than asked for: the preview's frame states what it is showing.
    queryFn: async () => {
      const pdf = await api.fetchIncidentReportPdf(productId!, sprintId!);

      return { url: URL.createObjectURL(pdf), size: pdf.size };
    },
    enabled: !!productId && !!sprintId,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });

/** Prepares a report again after its entries were corrected; availability goes with it. */
export const useRefreshIncidentReport = (): ((productId: string, sprintId: string) => void) => {
  const queryClient = useQueryClient();

  return (productId, sprintId) => {
    const key = [queryKeys.INCIDENT_REPORT_PDF, productId, sprintId];
    // The blob the browser is holding for the old document, before its URL is thrown away with the entry.
    const stale = queryClient.getQueryData<{ url: string }>(key);
    if (stale?.url) URL.revokeObjectURL(stale.url);

    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: [queryKeys.INCIDENT_REPORT_AVAILABILITY] });
  };
};

/** The mail the send would put out, composed only once the selection has stopped moving. */
export const useIncidentReportDraft = (
  organizationId?: string,
  productIds: string[] = [],
  sprintIds: string[] = [],
  enabled = false,
  /** Whose copy to render, for the greeting: the first recipient stands for all of them. */
  firstName = '',
): UseQueryResult<IncidentReportDraft, IError> =>
  useQuery({
    // The name is not in the key: keying on it re-renders the mail whenever a recipient is picked.
    queryKey: [queryKeys.INCIDENT_REPORT_DRAFT, organizationId, [...productIds].sort(), [...sprintIds].sort()],
    queryFn: () => api.fetchIncidentReportDraft(organizationId!, productIds, sprintIds, firstName || undefined),
    enabled: enabled && !!organizationId && productIds.length > 0 && sprintIds.length > 0,
    staleTime: Infinity,
  });

export const useSendIncidentReports = (
  organizationId?: string,
): UseMutationResult<IncidentReportSendResult, IError, IncidentReportSendInput, unknown> =>
  useMutation({
    mutationFn: body => api.sendIncidentReports(organizationId!, body),
  });

/** The chosen products' tagged managers, offered as recipients. Asked again when the products change. */
export const useProductManagerRecipients = (
  organizationId?: string,
  productIds: string[] = [],
): UseQueryResult<ListResponse<ProductManager>, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_MANAGER_RECIPIENTS, organizationId, [...productIds].sort()],
    queryFn: () => api.fetchProductManagerRecipients(organizationId!, productIds),
    enabled: !!organizationId && productIds.length > 0,
  });
